import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useJournalsApi } from 'core/contexts/JournalsApiContext/JournalsApiContext'
import { useJournalsAll } from 'core/hooks/useJournalsAll'
import { useFilters } from 'core/contexts/FilterContext'
import { handleSearchParam } from 'core/utils'
import { requestAccessToJournal, useJournalsSharedWithMe } from 'core/api/journals'
import { useNotifications } from 'shared/Notifications'
import { AxiosError } from 'axios'
import { useRecoilState } from 'recoil'
import { filterState } from 'core/store'

export default function useHomePageSearchParamsResolver() {
  const resolved = useInitialResolving()
  useSyncSearchParamsWithFilters({ skip: !resolved })
}

function useInitialResolving() {
  const resolvedMap = useRef({
    modal: false,
    filters: false,
    journals: false,
    sharedJournals: false,
  })

  useResolvingModal({
    skip: resolvedMap.current.modal,
    markAsResolved: () => (resolvedMap.current.modal = true),
  })
  useResolvingFilters({
    skip: resolvedMap.current.filters,
    markAsResolved: () => (resolvedMap.current.filters = true),
  })
  useResolvingJournals({
    skip: resolvedMap.current.journals,
    markAsResolved: () => (resolvedMap.current.journals = true),
  })
  useResolvingSharedJournals({
    skip: resolvedMap.current.journals,
    markAsResolved: () => (resolvedMap.current.sharedJournals = true),
  })

  return resolvedMap.current.modal && resolvedMap.current.filters && resolvedMap.current.journals
}

function useResolvingModal(params: { skip: boolean; markAsResolved: () => void }) {
  const { openAddDialog } = useJournalsApi()
  const navigate = useNavigate()

  useEffect(() => {
    if (params.skip) return
    let searchParams = new URLSearchParams(window.location.search)
    searchParams = handleSearchParam(['modal'], searchParams, ([modalName]) => {
      if (!modalName) return
      if (modalName === 'add_journal') openAddDialog()
    })

    params.markAsResolved()
    navigate({ search: searchParams.toString() }, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

function useResolvingFilters(params: { skip: boolean; markAsResolved: () => void }) {
  const navigate = useNavigate()

  const { setFiltersBySearchParams } = useFilters()

  useEffect(() => {
    if (params.skip) return
    let searchParams = new URLSearchParams(window.location.search)
    searchParams = setFiltersBySearchParams(searchParams)
    params.markAsResolved()
    navigate({ search: searchParams.toString() }, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

function useResolvingJournals(params: { skip: boolean; markAsResolved: () => void }) {
  const navigate = useNavigate()
  const journalsAll = useJournalsAll()
  const { setJournalBySearchParams } = useFilters()

  useEffect(() => {
    if (params.skip) return
    if (journalsAll.isLoading) return
    if (!journalsAll.data) return

    let searchParams = new URLSearchParams(window.location.search)
    searchParams = setJournalBySearchParams(searchParams, journalsAll.data)

    params.markAsResolved()
    navigate({ search: searchParams.toString() }, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

function useResolvingSharedJournals({
  skip,
  markAsResolved,
}: {
  skip: boolean
  markAsResolved: () => void
}) {
  const navigate = useNavigate()
  const { showNotification } = useNotifications()
  const journalsSharedWithMe = useJournalsSharedWithMe()
  const [filters, setFilters] = useRecoilState(filterState)

  useEffect(() => {
    if (skip) return

    const handleJournalAccess = async (journalId: string, sharingToken: string) => {
      try {
        await requestAccessToJournal(journalId, sharingToken)
        showNotification({ text: 'Journal added successfully', type: 'success' })
        await journalsSharedWithMe.mutate()
        setFilters({ ...filters, journalId })
      } catch (err) {
        let text = 'Something went wrong. Journal was not added'
        if (err instanceof AxiosError && err.response?.data?.message) {
          text = err.response.data.message
        }
        showNotification({ text, type: 'error' })
      } finally {
        markAsResolved()
        navigate({ search: searchParams.toString() }, { replace: true })
      }
    }

    let searchParams = new URLSearchParams(window.location.search)
    searchParams = handleSearchParam(
      ['sharedJournalId', 'sharingToken'],
      searchParams,
      ([journalId, sharingToken]) => {
        if (journalId && sharingToken) {
          handleJournalAccess(journalId, sharingToken)
        }
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

/** Read filters & update search params on each change */
function useSyncSearchParamsWithFilters({ skip = false }) {
  const { filtersSearchParams } = useFilters()
  const navigate = useNavigate()

  useEffect(() => {
    if (skip) return
    navigate({ search: filtersSearchParams.toString() }, { replace: true })
  }, [filtersSearchParams, navigate, skip])
}
